/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { IRemoteEquipment } from "../../../store/api/devices/types";
import {
  setSelectedEquipmentType,
  setSelectedStatus,
  setSelectedBand,
  setSearchText,
} from "../../../store/filters/dasInventory/reducer";
import {
  dasInventoryEquipmentTypeSelector,
  dasInventoryStatusSelector,
  dasInventoryBandSelector,
  dasInventorySearchTextSelector,
} from "../../../store/filters/dasInventory/selectors";
import { IFilterSelectBox } from "../../../components/Shared/Filter/types";
import { resolveDeviceStatusLabel } from "../../../utils/deviceStatus";
import { SxProps } from "@mui/material";

interface IUseDasInventoryFilterProps {
  data: IRemoteEquipment[];
}

export const useDasInventoryFilter = ({ data }: IUseDasInventoryFilterProps) => {
  const dispatch = useStoreDispatch();
  
  const selectedEquipmentType = useSelector(dasInventoryEquipmentTypeSelector);
  const selectedStatus = useSelector(dasInventoryStatusSelector);
  const selectedBand = useSelector(dasInventoryBandSelector);
  const searchText = useSelector(dasInventorySearchTextSelector);

  // Extract unique values from the data dynamically
  const uniqueEquipmentTypes = useMemo(() => {
    const types = new Set<string>();
    data.forEach((item) => {
      if (item.equipmentType) {
        types.add(item.equipmentType);
      }
    });
    return Array.from(types).sort();
  }, [data]);

  const uniqueStatuses = useMemo(() => {
    const statuses = new Set<string>();
    data.forEach((item) => {
      if (item.status) {
        const statusLabel = resolveDeviceStatusLabel(item.status);
        statuses.add(statusLabel);
      }
    });
    return Array.from(statuses).sort();
  }, [data]);

  const uniqueBands = useMemo(() => {
    const bands = new Set<string>();
    data.forEach((item) => {
      if (item.band) {
        bands.add(item.band);
      }
    });
    return Array.from(bands).sort();
  }, [data]);

  const formControlSx: SxProps = {
    width: "200px",
    "& .MuiSelect-select": {
      color: "var(--info-light)",
    },
  };

  const filters: IFilterSelectBox[] = [
    {
      id: "equipmentType",
      label: "TYPE",
      value: selectedEquipmentType,
      setValue: setSelectedEquipmentType,
      values: uniqueEquipmentTypes,
      width: "180px",
    },
    {
      id: "status",
      label: "STATUS",
      value: selectedStatus,
      setValue: setSelectedStatus,
      values: uniqueStatuses,
      width: "180px",
    },
    {
      id: "band",
      label: "BANDS",
      value: selectedBand,
      setValue: setSelectedBand,
      values: uniqueBands,
      width: "150px",
    },
  ];

  return {
    dispatch,
    filters,
    formControlSx,
    searchText,
    setSearchText,
  };
};
